import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronLeft, ChevronRight, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { players as mockPlayers } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image URLs - /uploads/* paths are served from backend
const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/uploads/')) {
    return `${API_URL}/api${imagePath}`;
  }
  return imagePath;
};

const PlayersSection = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [featuredPlayers, setFeaturedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch home players from API (max 5 with show_in_home=true)
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/players/home`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setFeaturedPlayers(data);
          } else {
            // Fallback to featured players if no home players
            const featuredResponse = await fetch(`${API_URL}/api/players/featured`);
            if (featuredResponse.ok) {
              const featuredData = await featuredResponse.json();
              setFeaturedPlayers(featuredData.slice(0, 5));
            } else {
              setFeaturedPlayers(mockPlayers.slice(0, 4));
            }
          }
        } else {
          setFeaturedPlayers(mockPlayers.slice(0, 4));
        }
      } catch (err) {
        // Fallback to mock data on error
        setFeaturedPlayers(mockPlayers.slice(0, 4));
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const goToPlayer = (index) => {
    setActiveIndex(index);
  };

  const nextPlayer = () => {
    if (featuredPlayers.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % featuredPlayers.length);
  };

  const prevPlayer = () => {
    if (featuredPlayers.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + featuredPlayers.length) % featuredPlayers.length);
  };

  // Touch/Mouse handlers for smooth swipe
  const handleStart = (clientX) => {
    setIsDragging(true);
    setStartX(clientX);
  };

  const handleMove = (clientX) => {
    if (!isDragging) return;
    const diff = startX - clientX;
    setScrollLeft(diff);
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (Math.abs(scrollLeft) > 80) {
      if (scrollLeft > 0) nextPlayer();
      else prevPlayer();
    }
    setScrollLeft(0);
  };

  if (loading) {
    return (
      <section id="jugadores" className="relative py-16 lg:py-28 bg-black overflow-hidden">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (featuredPlayers.length === 0) {
    return null;
  }

  const currentPlayer = featuredPlayers[activeIndex];

  return (
    <section
      id="jugadores"
      ref={sectionRef}
      className="relative py-16 lg:py-28 bg-black overflow-hidden"
      data-testid="players-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div ref={titleRef} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 lg:mb-12">
          <div>
            <span className="inline-block text-white/50 text-xs font-light tracking-[0.3em] uppercase mb-4">
              Jugadores Destacados
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
              Nuestros{' '}
              <span className="text-white/50 font-medium">Representados</span>
            </h2>
          </div>
          <Link 
            to="/jugadores"
            className="group inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white font-light transition-all duration-300"
            data-testid="view-all-players-btn"
          >
            <span>Ver todos los jugadores</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Main Player Display */}
        <div className="relative">
          {/* Mobile/Tablet: Card Carousel */}
          <div 
            ref={carouselRef}
            className="lg:hidden relative"
            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
            onTouchMove={(e) => handleMove(e.touches[0].clientX)}
            onTouchEnd={handleEnd}
            onMouseDown={(e) => handleStart(e.clientX)}
            onMouseMove={(e) => handleMove(e.clientX)}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
          >
            {/* Cards Container */}
            <div className="relative h-[500px] sm:h-[550px]">
              {featuredPlayers.map((player, index) => {
                const offset = index - activeIndex;
                const isActive = index === activeIndex;
                const isPrev = offset === -1 || (activeIndex === 0 && index === featuredPlayers.length - 1);
                const isNext = offset === 1 || (activeIndex === featuredPlayers.length - 1 && index === 0);
                
                let transform = 'translateX(100%) scale(0.85)';
                let zIndex = 0;
                let opacity = 0;
                
                if (isActive) {
                  transform = `translateX(${-scrollLeft * 0.5}px) scale(1)`;
                  zIndex = 10;
                  opacity = 1;
                } else if (isPrev) {
                  transform = 'translateX(-85%) scale(0.85)';
                  zIndex = 5;
                  opacity = 0.5;
                } else if (isNext) {
                  transform = 'translateX(85%) scale(0.85)';
                  zIndex = 5;
                  opacity = 0.5;
                }

                return (
                  <div
                    key={player.id}
                    className="absolute inset-0 transition-all duration-500 ease-out cursor-grab active:cursor-grabbing"
                    style={{
                      transform,
                      zIndex,
                      opacity,
                      pointerEvents: isActive ? 'auto' : 'none'
                    }}
                  >
                    <div className="relative h-full rounded-2xl overflow-hidden mx-4">
                      <img
                        src={resolveImageUrl(player.image)}
                        alt={player.name}
                        className="w-full h-full object-cover object-top"
                        draggable={false}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                      
                      {/* Player Info */}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center gap-2 text-white/50 text-sm font-light mb-2">
                          <MapPin size={14} />
                          <span>{player.club || 'Sin equipo'}</span>
                        </div>
                        <h3 className="text-3xl sm:text-4xl font-light text-white mb-2">
                          {player.name}
                        </h3>
                        <div className="flex items-center gap-4 text-white/60 font-light mb-4">
                          <span>{player.position}</span>
                          <span className="w-1 h-1 bg-white/30 rounded-full" />
                          <span>{player.country}</span>
                        </div>
                        {player.has_page && player.slug && (
                          <Link
                            to={`/jugadores/${player.slug}`}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-light transition-colors border border-white/20"
                          >
                            <span>Ver perfil</span>
                            <ArrowRight size={16} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Swipe Indicator */}
            <div className="flex items-center justify-center gap-6 mt-6">
              <button
                onClick={prevPlayer}
                className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white active:scale-95 transition-transform"
                data-testid="players-prev-mobile"
              >
                <ChevronLeft size={24} />
              </button>
              
              <div className="flex items-center gap-2">
                <ChevronLeft size={16} className="text-white/30 animate-pulse-left" />
                <span className="text-white/40 text-sm font-light">Desliza</span>
                <ChevronRight size={16} className="text-white/30 animate-pulse-right" />
              </div>
              
              <button
                onClick={nextPlayer}
                className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white active:scale-95 transition-transform"
                data-testid="players-next-mobile"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Thumbnails Navigation */}
            <div className="flex items-center justify-center gap-3 mt-6 px-4">
              {featuredPlayers.map((player, index) => (
                <button
                  key={player.id}
                  onClick={() => goToPlayer(index)}
                  className={`relative overflow-hidden rounded-lg transition-all duration-300 ${
                    index === activeIndex 
                      ? 'w-14 h-14 ring-2 ring-white' 
                      : 'w-12 h-12 opacity-50 hover:opacity-80'
                  }`}
                  data-testid={`player-thumb-${index}`}
                >
                  <img
                    src={resolveImageUrl(player.image)}
                    alt={player.name}
                    className="w-full h-full object-cover object-top"
                  />
                </button>
              ))}
            </div>

            {/* Ver todos - Mobile */}
            <div className="mt-8 text-center">
              <Link 
                to="/jugadores"
                className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white font-light transition-all duration-300"
                data-testid="view-all-players-btn-mobile"
              >
                <span>Ver todos los jugadores</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          {/* Desktop: Fixed Height Layout */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-12 items-start">
            {/* Player Image - Fixed Aspect Ratio */}
            <div className="relative">
              <div className="relative aspect-[3/4] max-h-[600px] rounded-2xl overflow-hidden">
                {featuredPlayers.map((player, index) => (
                  <div
                    key={player.id}
                    className="absolute inset-0 transition-all duration-700 ease-out"
                    style={{
                      opacity: index === activeIndex ? 1 : 0,
                      transform: index === activeIndex ? 'scale(1)' : 'scale(1.05)'
                    }}
                  >
                    <img
                      src={resolveImageUrl(player.image)}
                      alt={player.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                  </div>
                ))}

                {/* Navigation Arrows */}
                <button
                  onClick={prevPlayer}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 border border-white/10 group"
                  data-testid="players-prev-desktop"
                >
                  <ChevronLeft size={28} className="group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <button
                  onClick={nextPlayer}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 border border-white/10 group"
                  data-testid="players-next-desktop"
                >
                  <ChevronRight size={28} className="group-hover:translate-x-0.5 transition-transform" />
                </button>

                {/* Counter */}
                <div className="absolute top-4 right-4 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
                  <span className="text-white font-light">
                    {String(activeIndex + 1).padStart(2, '0')} / {String(featuredPlayers.length).padStart(2, '0')}
                  </span>
                </div>
              </div>
            </div>

            {/* Player Info - Fixed Height Container */}
            <div className="flex flex-col justify-center h-[600px]">
              <div className="flex items-center gap-2 text-white/40 text-sm font-light mb-4">
                <MapPin size={16} />
                <span>{currentPlayer?.club || 'Sin equipo'}</span>
              </div>

              {/* Fixed Height Name Container */}
              <div className="h-[180px] mb-6">
                <h3 className="text-5xl xl:text-6xl font-light text-white leading-tight">
                  {currentPlayer?.name}
                </h3>
              </div>

              <div className="flex items-center gap-8 mb-10">
                <div>
                  <p className="text-white/30 text-xs font-light uppercase tracking-wider mb-1">Posición</p>
                  <p className="text-white text-lg font-light">{currentPlayer?.position}</p>
                </div>
                <div className="w-px h-10 bg-white/10" />
                <div>
                  <p className="text-white/30 text-xs font-light uppercase tracking-wider mb-1">País</p>
                  <p className="text-white text-lg font-light">{currentPlayer?.country}</p>
                </div>
                {currentPlayer?.age && (
                  <>
                    <div className="w-px h-10 bg-white/10" />
                    <div>
                      <p className="text-white/30 text-xs font-light uppercase tracking-wider mb-1">Edad</p>
                      <p className="text-white text-lg font-light">{currentPlayer.age} años</p>
                    </div>
                  </>
                )}
              </div>

              {/* Ver perfil button - if player has page */}
              {currentPlayer?.has_page && currentPlayer?.slug && (
                <div className="mb-8">
                  <Link
                    to={`/jugadores/${currentPlayer.slug}`}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-white text-black font-medium rounded-full hover:bg-white/90 transition-all duration-300"
                  >
                    <span>Ver perfil completo</span>
                    <ArrowRight size={18} />
                  </Link>
                </div>
              )}

              {/* Thumbnails */}
              <div className="flex items-center gap-4">
                {featuredPlayers.map((player, index) => (
                  <button
                    key={player.id}
                    onClick={() => goToPlayer(index)}
                    className={`relative overflow-hidden rounded-xl transition-all duration-300 ${
                      index === activeIndex 
                        ? 'w-20 h-24 ring-2 ring-white' 
                        : 'w-16 h-20 opacity-40 hover:opacity-70'
                    }`}
                  >
                    <img
                      src={resolveImageUrl(player.image)}
                      alt={player.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </button>
                ))}
              </div>

              {/* Ver todos - Desktop */}
              <div className="mt-8">
                <Link 
                  to="/jugadores"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white font-light transition-all duration-300"
                  data-testid="view-all-players-btn-desktop"
                >
                  <span>Ver todos los jugadores</span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlayersSection;
